import React, { useEffect, useState } from 'react';
import ConfigPanelBody from '../ConfigPanelBody';
import ConfigPanelFooter from '../ConfigPanelFooter';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import OperationValueSelector from './OperationValueSelector';
import { useOperator } from './OperatorProvider';
import FormGroup from '../../../generic/FormGroup';
import { contentTypes, OPERATION_COMMANDS } from '../../../../constants';
import valueSources from '../../../../sources/value';
import typeSources from '../../../../sources/type';
import Loader from '../../../Loader';
import { getByID } from '../../../../utils/core';

const SORT_DIRECTION = {
    ascending: 'ASCENDING',
    descending: 'DESCENDING',
};

export const getVisibleApplyCommands = (valueToApply) => {
    if (valueToApply === null) {
        return [];
    }

    const applyContentType = valueToApply?.contentType?.toUpperCase() ?? '';

    switch (applyContentType) {
        case contentTypes.string:
            return [
                OPERATION_COMMANDS.setEqual,
                OPERATION_COMMANDS.empty,
                OPERATION_COMMANDS.new,
                OPERATION_COMMANDS.join,
            ];

        case contentTypes.number:
            return [
                OPERATION_COMMANDS.setEqual,
                OPERATION_COMMANDS.empty,
                OPERATION_COMMANDS.new,
                OPERATION_COMMANDS.add,
                OPERATION_COMMANDS.subtract,
                OPERATION_COMMANDS.divide,
                OPERATION_COMMANDS.multiply,
                OPERATION_COMMANDS.modulo,
            ];

        case contentTypes.list:
            return [
                OPERATION_COMMANDS.setEqual,
                OPERATION_COMMANDS.empty,
                OPERATION_COMMANDS.new,
                OPERATION_COMMANDS.update,
                OPERATION_COMMANDS.remove,
                OPERATION_COMMANDS.sort,
            ];

        case contentTypes.boolean:
        case contentTypes.object:
        case contentTypes.password:
        case contentTypes.encrypted:
        case contentTypes.content:
        case contentTypes.dateTime:
        default:
            return [OPERATION_COMMANDS.setEqual, OPERATION_COMMANDS.empty, OPERATION_COMMANDS.new];
    }
};

export const getVisibleReferenceCommands = (valueToApply, showReferenceValueSelectors) => {
    if (valueToApply === null || showReferenceValueSelectors === false) {
        return [];
    }

    const applyContentType = valueToApply?.contentType?.toUpperCase() ?? '';

    switch (applyContentType) {
        case contentTypes.number:
            return [OPERATION_COMMANDS.valueOf, OPERATION_COMMANDS.getLength];

        case contentTypes.object:
            return [
                OPERATION_COMMANDS.valueOf,
                OPERATION_COMMANDS.getFirst,
                OPERATION_COMMANDS.getNext,
            ];

        case contentTypes.boolean:
        case contentTypes.string:
        case contentTypes.password:
        case contentTypes.encrypted:
        case contentTypes.content:
        case contentTypes.list:
        case contentTypes.dateTime:
        default:
            return [OPERATION_COMMANDS.valueOf];
    }
};

export const getValidReferenceContentType = (
    valueToApply,
    valueToApplyCommand,
    valueToReferenceCommand,
    showReferenceValueSelectors,
) => {
    if (valueToApply === null || showReferenceValueSelectors === false) {
        return null;
    }

    const applyContentType = valueToApply?.contentType?.toUpperCase() ?? '';

    switch (applyContentType) {
        case contentTypes.number:
            return valueToReferenceCommand === OPERATION_COMMANDS.getLength.command
                ? contentTypes.list
                : contentTypes.number;

        case contentTypes.object:
            return valueToReferenceCommand === OPERATION_COMMANDS.getFirst.command ||
                valueToReferenceCommand === OPERATION_COMMANDS.getNext.command
                ? contentTypes.list
                : contentTypes.object;

        case contentTypes.list:
            return valueToApplyCommand === OPERATION_COMMANDS.update.command ||
                valueToApplyCommand === OPERATION_COMMANDS.remove.command
                ? contentTypes.object
                : contentTypes.list;

        case contentTypes.boolean:
        case contentTypes.string:
        case contentTypes.password:
        case contentTypes.encrypted:
        case contentTypes.content:
        case contentTypes.dateTime:
        default:
            return applyContentType;
    }
};

const OperationDetails = () => {
    const {
        mapElement,
        selectedOperationIndex,
        setCurrentScreen,
        valueReferences,
        OPERATOR_SCREENS,
        setSelectedOperationIndex,
        authenticationToken,
        tenantId,
        notifyError,
    } = useOperator();

    const operation = mapElement.operations[selectedOperationIndex] ?? null;

    /**
     * State values
     */

    const [isLoadingValues, setIsLoadingValues] = useState(operation !== null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Value to apply state
    // The value object that the operation operates on
    const [valueToApply, setValueToApply] = useState(null);
    // The command (string) that will be applied to the value
    const [valueToApplyCommand, setValueToApplyCommand] = useState(
        operation?.valueElementToApplyId?.command ?? '',
    );

    // Value to reference state
    // The reference value object that's used to operate
    const [valueToReference, setValueToReference] = useState(null);
    // The command (string) that will be used to retrieve a value
    const [valueToReferenceCommand, setValueToReferenceCommand] = useState(
        operation?.valueElementToReferenceId?.command ?? '',
    );

    // Sorting state
    const [sortDirection, setSortDirection] = useState(
        operation?.valueElementToApplySortDirection ?? SORT_DIRECTION.ascending,
    );
    // A list of all properties of the valueToApply's type
    const [sortProperties, setSortProperties] = useState([]);
    // The property of the valueToApply to sort by
    const [selectedSortProperty, setSelectedSortProperty] = useState(null);

    // The id of the type of the valueToReference
    const [referenceTypeId, setReferenceTypeId] = useState('');

    /**
     * Functions
     */

    // Checks the validity of the form
    const checkValid = () => {
        if (valueToApply === null || valueToApplyCommand === '') {
            return false;
        }

        if (
            valueToApplyCommand === OPERATION_COMMANDS.sort.command &&
            selectedSortProperty === null
        ) {
            return false;
        }

        if (
            valueToApplyCommand === OPERATION_COMMANDS.setEqual.command ||
            valueToApplyCommand === OPERATION_COMMANDS.remove.command ||
            valueToApplyCommand === OPERATION_COMMANDS.update.command ||
            valueToApplyCommand === OPERATION_COMMANDS.add.command ||
            valueToApplyCommand === OPERATION_COMMANDS.subtract.command ||
            valueToApplyCommand === OPERATION_COMMANDS.divide.command ||
            valueToApplyCommand === OPERATION_COMMANDS.multiply.command ||
            valueToApplyCommand === OPERATION_COMMANDS.join.command ||
            valueToApplyCommand === OPERATION_COMMANDS.modulo.command
        ) {
            if (valueToReference === null || valueToReferenceCommand === '') {
                return false;
            }
        }

        return true;
    };

    const onSave = () => {
        setHasSubmitted(true);
        if (checkValid()) {
            const operationToSave = {
                macroElementToExecuteDeveloperName: null,
                macroElementToExecuteId: null,
                valueElementToApplyDeveloperName: valueToApply?.developerName ?? null,
                valueElementToApplyContentType: valueToApply?.contentType ?? null,
                valueElementToApplyId: {
                    command: valueToApplyCommand ?? null,
                    id: valueToApply?.id ?? null,
                    typeElementPropertyId: valueToApply?.typeElementPropertyId ?? null,
                },
                valueElementToApplyPropertyDeveloperName:
                    valueToApply?.typeElementPropertyDeveloperName ?? null,
                valueElementToApplySortById: selectedSortProperty?.id ?? null,
                valueElementToApplySortByDeveloperName: selectedSortProperty?.developerName ?? null,
                valueElementToApplySortDirection: sortDirection,
                valueElementToReferenceDeveloperName: valueToReference?.developerName ?? null,
                valueElementToReferenceContentType: valueToReference?.contentType ?? null,
                valueElementToReferenceId: null,
                valueElementToReferencePropertyDeveloperName:
                    valueToReference?.typeElementPropertyDeveloperName ?? null,
            };

            if (valueToReference) {
                operationToSave.valueElementToReferenceId = {
                    command: valueToReferenceCommand ?? null,
                    id: valueToReference?.id ?? null,
                    typeElementPropertyId: valueToReference?.typeElementPropertyId ?? null,
                };
            }

            if (selectedOperationIndex) {
                mapElement.operations[selectedOperationIndex] = operationToSave;
            } else {
                mapElement.operations.push(operationToSave);
            }

            setCurrentScreen(OPERATOR_SCREENS.configuration);
        }
    };

    const onCancel = () => {
        setCurrentScreen(OPERATOR_SCREENS.configuration);
        setSelectedOperationIndex(null);
    };

    const onChangeValueToApply = (newValue) => {
        setValueToApply(newValue);
        
        if(newValue === undefined || newValue.id !== valueToApply?.id) {
            // Reset other form controls
            setValueToApplyCommand('');
            setValueToReference(null);
            setValueToReferenceCommand('');
        }
    };

    const onChangeApplyCommand = ({ target: { value } }) => {
        setValueToApplyCommand(value);
    };

    const onChangeReferenceCommand = ({ target: { value } }) => {
        setValueToReferenceCommand(value);
    };

    const onChangeSortProperty = ({ target: { value } }) => {
        const property = getByID(value, sortProperties);
        setSelectedSortProperty(property);
    };

    const onChangeSortDirection = ({ target: { value } }) => setSortDirection(value);

    /**
     * Computed values
     */

    // Only show the apply command selector when a value has been chosen because the options are dependant on the chosen value
    const showApplyCommandSelector = valueToApply !== null;
    // Only show sort selectors if if sort command is selected
    const showSortSelectors =
        valueToApplyCommand === OPERATION_COMMANDS.sort.command && sortProperties.length > 0;
    // Only show reference value selector when applicable commands are selected
    const showReferenceValueSelectors = [
        OPERATION_COMMANDS.setEqual.command,
        OPERATION_COMMANDS.remove.command,
        OPERATION_COMMANDS.add.command,
        OPERATION_COMMANDS.divide.command,
        OPERATION_COMMANDS.multiply.command,
        OPERATION_COMMANDS.modulo.command,
    ].includes(valueToApplyCommand);
    // Calculate the applicable command options
    const visibleApplyCommands = getVisibleApplyCommands(valueToApply);
    // Calculate the applicable reference commands
    const visibleReferenceCommands = getVisibleReferenceCommands(
        valueToApply,
        showReferenceValueSelectors,
    );

    // The content type of the reference value - used to filter the options in the reference value selector
    const referenceContentType = getValidReferenceContentType(
        valueToApply,
        valueToApplyCommand,
        valueToReferenceCommand,
        showReferenceValueSelectors,
    );

    /**
     * Effects
     */

    // Get the reference value's type id to pass into the value selector's filter
    useEffect(() => {
        if (
            referenceContentType === contentTypes.list ||
            referenceContentType === contentTypes.object
        ) {
            valueSources
                .get(valueToApply.id, authenticationToken, tenantId)
                .then((valueResponse) => {
                    setReferenceTypeId(valueResponse.typeElementId);
                })
                .catch(notifyError);
        } else {
            setReferenceTypeId('');
        }
    }, [valueToApplyCommand, valueToReferenceCommand]);

    // Get the properties of the selected value for sorting purposes
    useEffect(() => {
        if (valueToApply !== null && valueToApplyCommand === OPERATION_COMMANDS.sort.command) {
            valueSources
                .get(valueToApply.id, authenticationToken, tenantId)
                .then((valueResponse) => {
                    return typeSources.get(
                        valueResponse.typeElementId,
                        authenticationToken,
                        tenantId,
                    );
                })
                .then((type) => {
                    if (operation?.valueElementToApplySortById) {
                        const property = getByID(
                            operation.valueElementToApplySortById,
                            type.properties ?? [],
                        );
                        setSelectedSortProperty(property);
                    }

                    setSortProperties(type.properties);

                    if (operation?.valueElementToApplySortDirection) {
                        setSortDirection(operation.valueElementToApplySortDirection);
                    }
                })
                .catch(notifyError);
        } else {
            // Reset sort form controls
            setSortProperties([]);
            setSortDirection(SORT_DIRECTION.ascending);
            setSelectedSortProperty(null);
        }
    }, [valueToApply, valueToApplyCommand]);

    // Get the full value reference objects (valueToApply and valueToReference)
    useEffect(() => {
        if (isLoadingValues) {
            const valueToApplyId = operation?.valueElementToApplyId?.id ?? null;
            const valueToReferenceId = operation?.valueElementToReferenceId?.id ?? null;

            if (!valueToApplyId && !valueToReferenceId) {
                setIsLoadingValues(false);
                return;
            }

            const ids = [];
            if (valueToApplyId) {
                ids.push(valueToApplyId);
            }
            if (valueToReferenceId) {
                ids.push(valueToReferenceId);
            }

            valueSources
                .getList(authenticationToken, tenantId, {
                    id: ids,
                })
                .then((responseValueReferences) => {
                    if (valueToApplyId) {
                        const propertyId =
                            operation?.valueElementToApplyId?.typeElementPropertyId ?? null;
                        const reference = responseValueReferences.find(
                            (vr) =>
                                vr.id === valueToApplyId && vr.typeElementPropertyId === propertyId,
                        );

                        setValueToApply(reference);
                    }
                    if (valueToReferenceId) {
                        const propertyId =
                            operation?.valueElementToReferenceId?.typeElementPropertyId ?? null;
                        const reference = responseValueReferences.find(
                            (vr) =>
                                vr.id === valueToReferenceId &&
                                vr.typeElementPropertyId === propertyId,
                        );

                        setValueToReference(reference);
                    }
                    setIsLoadingValues(false);
                })
                .catch(notifyError);
        }
    }, []);

    if (isLoadingValues) {
        return <Loader />;
    }

    return (
        <>
            <ConfigPanelBody>
                <h3 className="margin-top-small">{`Operation for: ${mapElement.developerName}`}</h3>

                <FormGroup
                    label="Which Value do you want to change?"
                    isRequired
                    validationMessage="Please choose a Value"
                    isValid={valueToApply !== null}
                    showValidation={hasSubmitted}
                >
                    <OperationValueSelector
                        selectorId="valueToApply"
                        authenticationToken={authenticationToken}
                        tenantId={tenantId}
                        value={valueToApply ?? null}
                        propertyId={valueToApply?.typeElementPropertyId ?? null}
                        valueReferences={valueReferences}
                        onChange={onChangeValueToApply}
                        notifyError={notifyError}
                    />
                </FormGroup>

                {showApplyCommandSelector && (
                    <FormGroup
                        label="How do you want to change the Value?"
                        isRequired
                        validationMessage="Please choose an option"
                        isValid={valueToApplyCommand !== ''}
                        showValidation={hasSubmitted}
                    >
                        <select
                            className="form-control form-select form-control-width"
                            value={valueToApplyCommand}
                            onChange={onChangeApplyCommand}
                            data-testid="select-command"
                        >
                            {valueToApplyCommand === '' && (
                                <option key="empty" value="">
                                    Please select
                                </option>
                            )}
                            {visibleApplyCommands.map((commandType) => (
                                <option key={commandType.command} value={commandType.command}>
                                    {commandType.display}
                                </option>
                            ))}
                        </select>
                    </FormGroup>
                )}

                {showSortSelectors && (
                    <>
                        <FormGroup
                            label="The property to use for this sort"
                            isRequired
                            validationMessage="Please choose a property"
                            isValid={selectedSortProperty !== null}
                            showValidation={hasSubmitted}
                        >
                            <select
                                className="form-control form-select form-control-width"
                                value={selectedSortProperty?.id ?? ''}
                                onChange={onChangeSortProperty}
                            >
                                {selectedSortProperty === null && (
                                    <option key="empty" value="">
                                        Please select
                                    </option>
                                )}
                                {sortProperties.map((property) => (
                                    <option key={property.id} value={property.id}>
                                        {property.developerName}
                                    </option>
                                ))}
                            </select>
                        </FormGroup>
                        <FormGroup label="Sort direction">
                            <select
                                className="form-control form-select form-control-width"
                                value={sortDirection}
                                onChange={onChangeSortDirection}
                            >
                                <option value={SORT_DIRECTION.ascending}>Ascending</option>
                                <option value={SORT_DIRECTION.descending}>Descending</option>
                            </select>
                        </FormGroup>
                    </>
                )}

                {showReferenceValueSelectors && (
                    <>
                        <FormGroup
                            label="How do you want to retrieve the reference Value?"
                            isRequired
                            validationMessage="Please choose an option"
                            isValid={valueToReferenceCommand !== ''}
                            showValidation={hasSubmitted}
                        >
                            <select
                                className="form-control form-select form-control-width"
                                value={valueToReferenceCommand}
                                onChange={onChangeReferenceCommand}
                            >
                                {valueToReferenceCommand === '' && (
                                    <option key="empty" value="">
                                        Please select
                                    </option>
                                )}
                                {visibleReferenceCommands.map((commandType) => (
                                    <option key={commandType.command} value={commandType.command}>
                                        {commandType.display}
                                    </option>
                                ))}
                            </select>
                        </FormGroup>

                        <FormGroup
                            label="How do you want to retrieve the reference Value?"
                            isRequired
                            validationMessage="Please choose a Value"
                            isValid={valueToReference !== null}
                            showValidation={hasSubmitted}
                        >
                            <OperationValueSelector
                                selectorId="valueToReference"
                                authenticationToken={authenticationToken}
                                tenantId={tenantId}
                                value={valueToReference ?? null}
                                propertyId={valueToReference?.propertyId ?? null}
                                valueReferences={valueReferences}
                                onChange={setValueToReference}
                                contentTypeFilter={referenceContentType}
                                typeElementIdFilter={referenceTypeId}
                            />
                        </FormGroup>
                    </>
                )}
            </ConfigPanelBody>

            <ConfigPanelFooter>
                <ButtonDefault className="flex-child-right" onClick={onCancel}>
                    Cancel
                </ButtonDefault>
                <ButtonPrimary className="margin-left" onClick={onSave}>
                    {selectedOperationIndex === null ? 'Add' : 'Update'}
                </ButtonPrimary>
            </ConfigPanelFooter>
        </>
    );
};

export default OperationDetails;
